import { useMsal } from "@azure/msal-react";
import { loginRequest, appConfig } from "../authConfig";

/**
 * ตรวจสอบและจัดการ token ที่หมดอายุ
 * @param {Error} error - Error object จาก API call
 * @returns {boolean} - true ถ้าเป็น token expiration error
 */
export const isTokenExpiredError = (error) => {
  if (!error) return false;
  
  // ตรวจสอบ status code ที่บ่งบอกถึง token หมดอายุ
  const expiredStatusCodes = [401, 403];
  
  if (error.response && expiredStatusCodes.includes(error.response.status)) {
    return true;
  }
  
  // ตรวจสอบ error message ที่บ่งบอกถึง token หมดอายุ
  const expiredErrorMessages = [
    'token',
    'expired',
    'unauthorized',
    'forbidden',
    'authentication',
    'access_denied'
  ];
  
  const errorMessage = error.message?.toLowerCase() || '';
  const errorData = error.response?.data?.message?.toLowerCase() || '';
  
  return expiredErrorMessages.some(msg => 
    errorMessage.includes(msg) || errorData.includes(msg)
  );
};

/**
 * จัดการการ logout และ redirect ไปหน้า login
 */
export const handleTokenExpiredLogout = () => {
  // ล้าง session storage
  sessionStorage.clear();
  localStorage.removeItem("obj_sv_profile");
  localStorage.removeItem("userProfile");
  
  // Redirect ไปหน้า logout ของ Microsoft
  const logoutUrl = 
    "https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=" + 
    appConfig.appEndpoint.baseUrl;
    
  window.location.href = logoutUrl;
};

/**
 * Hook สำหรับจัดการ token refresh
 */
export const useTokenHandler = () => {
  const { instance, accounts } = useMsal();
  
  const refreshToken = async () => {
    try {
      if (accounts.length === 0) {
        throw new Error("No accounts available");
      }
      
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      
      // บันทึก token ใหม่
      sessionStorage.setItem("AccessToken", JSON.stringify({
        value: response.accessToken,
        expires: response.expiresOn
      }));
      
      return response.accessToken;
    } catch (error) {
      handleTokenExpiredLogout();
      return null;
    }
  };
  
  return { refreshToken };
};

/**
 * Wrapper function สำหรับ API calls ที่มีการจัดการ token expiration
 * @param {Function} apiCall - ฟังก์ชัน API ที่จะเรียก
 * @param {...any} args - arguments สำหรับ API call
 * @returns {Promise} - Promise result ของ API call
 */
export const withTokenHandling = async (apiCall, ...args) => {
  try {
    return await apiCall(...args);
  } catch (error) {
    if (isTokenExpiredError(error)) {
      handleTokenExpiredLogout();
      throw new Error("Token expired. Please login again.");
    }
    throw error;
  }
};
